/**
 * Modal component
 */

import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
  footer?: React.ReactNode;
  containerClassName?: string;
  /** Merged onto the outer fixed overlay — use for z-index when stacking above portaled UI (inline wins over class). */
  containerStyle?: React.CSSProperties;
  /** Replaces default inner frame (centering + padding) when set; otherwise `items-center justify-center p-4`. */
  contentFrameClassName?: string;
  /** Merged onto the white dialog panel after size classes (e.g. fullscreen overrides). */
  panelClassName?: string;
  /** Merged onto the body wrapper around `children` (default `p-6`). */
  bodyClassName?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  containerClassName,
  containerStyle,
  contentFrameClassName,
  panelClassName,
  bodyClassName,
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[96vw] w-full', // near full screen for template generation etc.
  };

  return (
    <div
      className={clsx('fixed inset-0 overflow-y-auto', containerClassName ?? 'z-50')}
      style={containerStyle}
    >
      <div
        className={
          contentFrameClassName ?? 'flex min-h-full items-center justify-center p-4'
        }
      >
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div
          className={clsx(
            'relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full border border-theme-border z-50',
            sizeClasses[size],
            panelClassName
          )}
        >
          {/* Header */}
          {title && (
            <div className="flex flex-shrink-0 items-center justify-between p-4 border-b border-theme-border">
              <h3 className="text-lg font-semibold text-theme-text">{title}</h3>
              <button
                onClick={onClose}
                className="text-theme-text-secondary hover:text-theme-text transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
          
          {/* Body */}
          <div className={clsx('p-6', bodyClassName)}>{children}</div>
          
          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-2 p-4 border-t border-theme-border bg-theme-surface">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


